import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { auth } from "../firebase";
import { stopTokenRefresh } from "./tokenRefresh";
import { isGuestUser } from "./guestSession";

const DEFAULT_INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];

const isLoggedInUser = () => {
  if (isGuestUser()) return false;
  try {
    const raw = localStorage.getItem("userData");
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Boolean(parsed?.access_token || parsed?.id_token);
  } catch {
    return false;
  }
};

export const logoutDueToInactivity = (dispatch, history) => {
  stopTokenRefresh();
  try {
    auth.signOut();
  } catch (_) {
    /* ignore */
  }
  localStorage.removeItem("userData");
  dispatch({ type: "LOGOUT", payload: null });
  toast.info("You were logged out due to inactivity.");
  if (history?.push) {
    history.push("/login");
  } else {
    window.location.assign("/login");
  }
};

/**
 * Logs out authenticated users after a period with no pointer/keyboard activity.
 */
export default function useInactivityLogout(dispatch) {
  const history = useHistory();
  const timerRef = useRef(null);
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  useEffect(() => {
    const timeoutMs = Number(process.env.REACT_APP_INACTIVITY_LOGOUT_MS) || DEFAULT_INACTIVITY_MS;
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return undefined;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleLogout = () => {
      clearTimer();
      if (!isLoggedInUser()) return;
      timerRef.current = setTimeout(() => {
        if (isLoggedInUser()) {
          logoutDueToInactivity(dispatchRef.current, history);
        }
      }, timeoutMs);
    };

    const onActivity = () => {
      if (!isLoggedInUser()) {
        clearTimer();
        return;
      }
      scheduleLogout();
    };

    scheduleLogout();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", onActivity);

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
      });
      document.removeEventListener("visibilitychange", onActivity);
    };
  }, [history]);
}
